import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LocalSysChartAcctMapService {

  constructor(private http: HttpClient) {}

  /**
   * Get System Account Dropdown
   * Action Code: 9bappg
   */
  getSystemAcctDropdown(baseUrl: string, params: any, globalService: any): Observable<any> {
    return this.makePostRequest(`${baseUrl}/loadJsonObject`, params, globalService);
  }

  /**
   * Get System Account Lookup
   * Action Code: 5gdshx
   */
  getSystemAcctLookup(baseUrl: string, params: any, globalService: any): Observable<any> {
    return this.makePostRequest(`${baseUrl}/loadJsonObject`, params, globalService);
  }

  /**
   * Load Tax Sensitive Data (non-FIR)
   * Action Code: 5rvabb (GET request)
   */
  loadTaxSensitiveData(baseUrl: string, taxYear: string, sysAcct: string, scenario: string, jcdKey: string): Observable<any> {
    const params = new HttpParams()
      .set('action_code', '5rvabb')
      .set('tax_year', taxYear || '')
      .set('scenario', scenario || '')
      .set('jcd_key', jcdKey || '')
      .set('sys_acct', sysAcct || '');

    return this.http.get<any>(`${baseUrl}/loadJsonObject`, {
      params,
      withCredentials: true,
    });
  }

  /**
   * Save Tax Sensitive Details (non-FIR)
   * Action Code: nbms3a
   */
  saveTaxSensitiveDetails(
    baseUrl: string,
    details: any[],
    settings: any,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    return this.postTaxSensitive(
      `${baseUrl}/saveJsonObject?action_code=nbms3a`,
      details, settings, globalService, filterParams,
      'Save_Tax_Sensitive_Details'
    );
  }

  /**
   * Delete Tax Sensitive Details (non-FIR)
   * Action Code: hhba21
   */
  deleteTaxSensitiveDetails(
    baseUrl: string,
    details: any[],
    settings: any,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    return this.postTaxSensitive(
      `${baseUrl}/saveJsonObject?action_code=hhba21`,
      details, settings, globalService, filterParams,
      'Delete_Tax_Sensitive_Details'
    );
  }

  /**
   * Get System Account Form Data (IRS Form table)
   * Action Code: 5hsimn (GET request)
   */
  getSystemAcctFormData(baseUrl: string, params: any, globalService: any): Observable<any> {
    return this.makeGetRequest(`${baseUrl}/loadJsonObject`, params, globalService);
  }

  /**
   * Get Template Data (SCHDM_TYPE + METHOD_OF_CALC dropdowns)
   * Action Code: kbaflc
   */
  getTemplateData(baseUrl: string, params: any, globalService: any): Observable<any> {
    return this.makePostRequest(`${baseUrl}/loadJsonObject`, params, globalService);
  }

  /**
   * Get Posting TAC Data (POSTING_TAC dropdown)
   * Action Code: iemg45
   */
  getPostingTacData(baseUrl: string, params: any, globalService: any): Observable<any> {
    return this.makePostRequest(`${baseUrl}/loadJsonObject`, params, globalService);
  }

  /**
   * Get Posting Acct Data (POSTING_ACCT dropdown)
   * Action Code: sdcmbv
   */
  getPostingAcctData(baseUrl: string, params: any, globalService: any): Observable<any> {
    return this.makePostRequest(`${baseUrl}/loadJsonObject`, params, globalService);
  }

  /**
   * Get TBBS Acct Data (TAX_STREAM_ACCT dropdown)
   * Action Code: wfk6zt
   */
  getTBBSAcctData(baseUrl: string, params: any, globalService: any): Observable<any> {
    return this.makePostRequest(`${baseUrl}/loadJsonObject`, params, globalService);
  }

  /**
   * Load Audit Data
   * Action Code: f67kgj
   */
  loadAuditData(baseUrl: string, params: any, globalService: any): Observable<any> {
    return this.makePostRequest(`${baseUrl}/loadJsonObject`, params, globalService);
  }

  /**
   * Save Form Details (mapping save)
   * Action Code: hhba21
   */
  saveFormDetails(
    baseUrl: string,
    localSystemAccounts: any[],
    saveTaxSensitiveData: any[],
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=hhba21`;

    const jsonObj = JSON.stringify(saveTaxSensitiveData);
    const jsonSettings = JSON.stringify(localSystemAccounts);

    let params: any = { ...filterParams };
    if (globalService?.globalParams) {
      params = { ...globalService.globalParams, ...params };
    }

    params.jsonObj = jsonObj;
    params.jsonSettings = jsonSettings;
    params.process_name = 'LOCAL_TO_SYSTEM_SAVE';

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true }).pipe(
      catchError(error => {
        console.error('Error in saveFormDetails:', error);
        throw error;
      })
    );
  }

  private postTaxSensitive(
    url: string,
    details: any[],
    settings: any,
    globalService: any,
    filterParams: any,
    processName: string
  ): Observable<any> {
    let params: any = { ...(globalService?.globalParams || {}), ...(filterParams || {}) };
    params.jsonObj = JSON.stringify(details || []);
    params.jsonSettings = JSON.stringify(settings || {});
    params.process_name = processName;

    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, String(value));
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  private makePostRequest(url: string, params: any, globalService: any): Observable<any> {
    let requestParams: any = { ...params };
    if (globalService?.globalParams) {
      requestParams = { ...globalService.globalParams, ...requestParams };
    }

    const urlEncoded = new URLSearchParams();
    Object.keys(requestParams).forEach(key => {
      if (requestParams[key] !== undefined && requestParams[key] !== null) {
        urlEncoded.set(key, requestParams[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  private makeGetRequest(url: string, params: any, globalService: any): Observable<any> {
    let requestParams: any = { ...params };
    if (globalService?.globalParams) {
      requestParams = { ...globalService.globalParams, ...requestParams };
    }

    const queryParams = new URLSearchParams();
    Object.keys(requestParams).forEach(key => {
      if (requestParams[key] !== undefined && requestParams[key] !== null) {
        queryParams.set(key, requestParams[key].toString());
      }
    });

    const fullUrl = `${url}?${queryParams.toString()}`;
    return this.http.get<any>(fullUrl, { withCredentials: true });
  }
}
